import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import { navigateToDmTaskidFbs } from '../../utils/navigateDmTaskidFbs';
import { navigateToTuvanNhathauDmTask } from '../../tuvan_nhathau/utils/navigateTuvanDmTaskid';

interface NotiItem {
  id: number;
  title: string;
  content: string;
  created_at: string;
  is_read: boolean;
  type?: string | null;
  reference_id?: number | null;
  reference_type?: string | null;
  category?: string;
}

const POLL_MS = 60_000;

type NotificationBellMode = 'pmis' | 'tvnt';

interface NotificationBellProps {
  mode?: NotificationBellMode;
  iconSx?: Record<string, any>;
}

const footerBtnSx = {
  flex: '1 1 0',
  minWidth: 0,
  maxHeight: 32,
  minHeight: 32,
  py: 0,
  px: 0.5,
  fontSize: '0.8125rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
};

const NotificationBell: React.FC<NotificationBellProps> = ({ mode = 'pmis', iconSx }) => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<NotiItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmMarkAllOpen, setConfirmMarkAllOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const isTvntMode = mode === 'tvnt';
  const endpointPrefix = isTvntMode ? '/tuvan-nhathau/notifications' : '/notifications';
  const listPath = isTvntMode ? '/tuvan_nhathau/notifications' : '/notifications';
  const settingsPath = isTvntMode ? '/tuvan_nhathau/setting#notification-settings' : '/profile-settings#notification-settings';

  const fetchCount = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${endpointPrefix}/unread-count`);
      if (res.data?.code === 0 && res.data?.data) {
        setCount(Number(res.data.data.count) || 0);
      }
    } catch {
      /* ignore */
    }
  }, [endpointPrefix]);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${endpointPrefix}/unread-top`, { params: { limit: 10 } });
      if (res.data?.code === 0 && res.data?.data?.items) {
        setItems(res.data.data.items);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [endpointPrefix]);

  useEffect(() => {
    fetchCount();
    const t = window.setInterval(fetchCount, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchCount();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchCount]);

  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    fetchPreview();
    fetchCount();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const markRead = async (ids: number[]) => {
    if (!ids.length) return;
    try {
      await axiosInstance.post(`${endpointPrefix}/mark-as-read`, { notification_ids: ids });
      fetchCount();
    } catch {
      /* ignore */
    }
  };

  const openMarkAllConfirm = () => {
    setConfirmMarkAllOpen(true);
  };

  const handleConfirmMarkAll = async () => {
    setMarkingAll(true);
    try {
      const res = await axiosInstance.post(`${endpointPrefix}/mark-all-as-read`);
      if (res.data?.code === 0) {
        setItems([]);
        await fetchCount();
      }
    } catch {
      /* ignore */
    } finally {
      setMarkingAll(false);
      setConfirmMarkAllOpen(false);
    }
  };

  const goFromNoti = async (row: NotiItem) => {
    await markRead([row.id]);
    handleClose();
    const { reference_type, reference_id } = row;
    if (!reference_type || reference_id == null) {
      navigate(listPath);
      return;
    }
    switch (reference_type) {
      case 'task':
        navigate(`/tasks/${reference_id}`);
        break;
      case 'incoming_document':
        navigate(`/xlvb/incoming-documents?submenu=van_ban_cho_xu_ly&doc_id=${reference_id}`);
        break;
      case 'outgoing_document':
        navigate(`/xlvb/outgoing-documents?submenu=van_ban_di_du_thao&doc_id=${reference_id}`);
        break;
      case 'event':
        navigate(`/calendar`);
        break;
      case 'dm_taskid':
        await navigateToDmTaskidFbs(navigate, reference_id);
        break;
      case 'tuvan_nhathau_dm_taskid':
        await navigateToTuvanNhathauDmTask(navigate, reference_id);
        break;
      case 'tuvan_nhathau_incoming_document':
        navigate(`/tuvan_nhathau/incoming/${reference_id}`);
        break;
      case 'tuvan_nhathau_outgoing_document':
        navigate(`/tuvan_nhathau/outgoing/${reference_id}`);
        break;
      default:
        navigate(listPath);
    }
  };

  return (
    <>
      <Tooltip
        title={count > 0 ? `Thông báo (${count} chưa đọc)` : 'Thông báo'}
        placement="bottom"
        arrow
        enterDelay={400}
      >
        <IconButton
          color="inherit"
          size="small"
          aria-label="Thông báo"
          onClick={handleOpen}
          sx={{ p: 0.5 }}
        >
          <Badge
            badgeContent={count > 99 ? '99+' : count}
            color="error"
            overlap="circular"
            invisible={count === 0}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 22, ...iconSx }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: 'calc(100vw - 24px)', mt: 1 },
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Thông báo chưa đọc
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Đang tải…
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Không có thông báo mới
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
            {items.map((row) => (
              <ListItemButton key={row.id} alignItems="flex-start" onClick={() => goFromNoti(row)}>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {row.title || 'Thông báo'}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {row.content}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <Box
          sx={{
            p: '4px',
            display: 'flex',
            width: '100%',
            '& > *': { flex: 1, minWidth: 0, display: 'flex' },
          }}
        >
          <Tooltip title="Mở trang danh sách thông báo đầy đủ" placement="top" arrow enterDelay={400}>
            <span style={{ display: 'flex', flex: 1, minWidth: 0 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => { handleClose(); navigate(listPath); }}
                sx={{
                  ...footerBtnSx,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                Xem hết
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              count === 0
                ? 'Không có thông báo chưa đọc'
                : 'Đánh dấu tất cả thông báo (theo loại đang bật trong cài đặt) là đã xem'
            }
            placement="top"
            arrow
            enterDelay={400}
          >
            <span style={{ display: 'flex', flex: 1, minWidth: 0 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                disabled={count === 0}
                onClick={openMarkAllConfirm}
                sx={{
                  ...footerBtnSx,
                  borderRadius: 0,
                  ml: '-1px',
                  fontWeight: 600,
                }}
              >
                Đã xem
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Cài đặt loại thông báo nhận được" placement="top" arrow enterDelay={400}>
            <span style={{ display: 'flex', flex: 1, minWidth: 0 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => { handleClose(); navigate(settingsPath); }}
                sx={{
                  ...footerBtnSx,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  ml: '-1px',
                }}
              >
                Cài đặt
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Popover>

      <Dialog
        open={confirmMarkAllOpen}
        onClose={() => !markingAll && setConfirmMarkAllOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <DialogTitle>Đánh dấu đã xem</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Bạn có chắc muốn đánh dấu tất cả thông báo (theo các loại đang bật trong cài đặt) là đã xem?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setConfirmMarkAllOpen(false)} disabled={markingAll}>
            Hủy
          </Button>
          <Button variant="contained" onClick={handleConfirmMarkAll} disabled={markingAll} autoFocus>
            {markingAll ? 'Đang xử lý…' : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationBell;
